// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Divider,
  Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, CircularProgress, Tooltip, Switch,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockResetIcon from '@mui/icons-material/LockReset';
import EditIcon from '@mui/icons-material/Edit';
import api from '../utils/api';
import { useAuthStore } from '../store';
import ChampRecherche from '../components/ChampRecherche';
import { filterItems } from '../utils/recherche';


const PERMS = [
  { key: 'perm_clients_write',  label: 'Modifier les clients',   desc: 'Créer et éditer les fiches clients' },
  { key: 'perm_clients_delete', label: 'Supprimer des clients',  desc: 'Supprimer définitivement des clients' },
  { key: 'perm_billing',        label: 'Facturation',            desc: 'Envoyer des factures et liens de paiement' },
  { key: 'perm_users_manage',   label: 'Gérer l\'équipe',        desc: 'Inviter, supprimer, modifier les permissions (admin)' },
];

export default function SettingsPage() {
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);

  // Invitation
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);

  // Suppression
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Refus du serveur (409 : références bloquantes). Il reste affiché dans le
  // dialogue, un bandeau de 4 s ne laisse pas le temps de lire la liste.
  const [deleteError, setDeleteError] = useState(null);

  // Permissions
  const [permTarget, setPermTarget] = useState(null); // copie de l'utilisateur en cours d'édition
  const [permSaving, setPermSaving] = useState(false);

  // Changement de mot de passe
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  // Recherche dans l'équipe
  const [recherche, setRecherche] = useState('');

  // Motif d'un refus de l'API. Sans réponse du serveur (réseau), le message
  // par défaut seul.
  const lireDetail = (e, defaultValue) => {
    const detail = e?.response?.data?.detail;
    if (typeof detail === 'string' && detail) return detail;
    if (Array.isArray(detail)) return detail.map((d) => d.msg).filter(Boolean).join(' ; ') || defaultValue;
    return e?.response ? `${defaultValue} (HTTP ${e.response.status})` : defaultValue;
  };

  const closeDeleteDialog = () => { setDeleteTarget(null); setDeleteError(null); };

  const flash = (type, msg) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback(null), 4000);
  };

  // Les deux lectures sont indépendantes : un compte sans droit sur l'équipe
  // voit quand même son propre compte.
  const load = async () => {
    setLoading(true);
    const [meRes, usersRes] = await Promise.allSettled([
      api.get('/auth/me'),
      api.get('/auth/users'),
    ]);
    if (meRes.status === 'fulfilled') setMe(meRes.value.data);
    if (usersRes.status === 'fulfilled') setUsers(usersRes.value.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleInvite = async () => {
    if (!inviteEmail) return;
    setInviting(true);
    try {
      await api.post('/auth/invite', { email: inviteEmail, full_name: inviteName || null });
      setInviteOpen(false);
      setInviteEmail(''); setInviteName('');
      flash('success', `Invitation envoyée à ${inviteEmail}`);
      load();
    } catch (e) {
      flash('error', e.response?.data?.detail || "Erreur lors de l'invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await api.patch(`/auth/users/${user.id}`, { is_active: !user.is_active });
      flash('success', `Compte ${!user.is_active ? 'activé' : 'désactivé'}`);
      load();
    } catch {
      // Refus : la ligne garde son état, rien n'est rechargé.
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete(`/auth/users/${deleteTarget.id}`);
      closeDeleteDialog(); flash('success', 'Utilisateur supprimé'); load();
    } catch (e) {
      setDeleteError(lireDetail(e, 'Suppression refusée'));
    } finally {
      setDeleting(false);
    }
  };

  const openPermDialog = (user) => setPermTarget({ ...user });

  const handlePermToggle = (key) => {
    setPermTarget(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSavePerms = async () => {
    setPermSaving(true);
    try {
      const payload = {};
      PERMS.forEach(p => { payload[p.key] = permTarget[p.key]; });
      await api.patch(`/auth/users/${permTarget.id}/permissions`, payload);
      setPermTarget(null);
      flash('success', `Permissions de ${permTarget.full_name || permTarget.email} mises à jour`);
      load();
    } catch (e) {
      flash('error', e.response?.data?.detail || 'Erreur');
    } finally {
      setPermSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (pwNew !== pwConfirm) { flash('error', 'Les mots de passe ne correspondent pas'); return; }
    if (pwNew.length < 8) { flash('error', 'Minimum 8 caractères'); return; }
    setPwLoading(true);
    try {
      const r = await api.post('/auth/change-password',
        { current_password: pwCurrent, new_password: pwNew });
      // Le serveur révoque les jetons émis avant le changement (claim pwh) et
      // rend un nouveau jeton : sans le reprendre ici, la requête suivante
      // prend un 401 et l'admin est déconnecté.
      const data = r.data || {};
      if (data.access_token) useAuthStore.getState().login(data.access_token);
      setPwCurrent(''); setPwNew(''); setPwConfirm('');
      flash('success', 'Mot de passe modifié');
    } catch (e) {
      flash('error', lireDetail(e, 'Erreur'));
    } finally {
      setPwLoading(false);
    }
  };

  const filteredTeam = useMemo(
    () => filterItems(users, recherche, (u) => [u.full_name, u.email]),
    [users, recherche],
  );

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  const isSuperAdmin = me?.perm_users_manage;

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" gutterBottom>Paramètres</Typography>

      {feedback && (
        <Alert severity={feedback.type} sx={{ mb: 3 }} onClose={() => setFeedback(null)}>
          {feedback.msg}
        </Alert>
      )}

      {/* Mon compte */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>Mon compte</Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">Email</Typography>
          <Typography fontWeight="bold">{me?.email}</Typography>
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">Nom</Typography>
          <Typography fontWeight="bold">{me?.full_name || '—'}</Typography>
        </Box>

        <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockResetIcon fontSize="small" /> Changer le mot de passe
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
          <TextField label="Mot de passe actuel" type="password" size="small" value={pwCurrent} onChange={e => setPwCurrent(e.target.value)} />
          <TextField label="Nouveau mot de passe" type="password" size="small" value={pwNew} onChange={e => setPwNew(e.target.value)} helperText="Minimum 8 caractères" />
          <TextField label="Confirmer" type="password" size="small" value={pwConfirm} onChange={e => setPwConfirm(e.target.value)} error={pwConfirm.length > 0 && pwNew !== pwConfirm} />
          <Button variant="contained" onClick={handleChangePassword} disabled={!pwCurrent || !pwNew || !pwConfirm || pwLoading} sx={{ alignSelf: 'flex-start' }}>
            {pwLoading ? <CircularProgress size={20} color="inherit" /> : 'Mettre à jour'}
          </Button>
        </Box>
      </Paper>

      {/* Équipe */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" fontWeight="bold">Équipe</Typography>
          <ChampRecherche
            valeur={recherche} onChange={setRecherche}
            placeholder="Rechercher un collaborateur par nom ou adresse"
            compte={filteredTeam.length} total={users.length} nomObjet="collaborateur"
          />
          {isSuperAdmin && (
            <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setInviteOpen(true)}>
              Inviter un collaborateur
            </Button>
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Nom</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Permissions</strong></TableCell>
              <TableCell><strong>Statut</strong></TableCell>
              {isSuperAdmin && <TableCell align="right"><strong>Actions</strong></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTeam.length === 0 && users.length > 0 && (
              <TableRow>
                <TableCell colSpan={isSuperAdmin ? 5 : 4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Aucun collaborateur ne correspond à cette recherche.
                </TableCell>
              </TableRow>
            )}
            {filteredTeam.map(u => {
              const isMe = me?.id === u.id;
              const activePerms = PERMS.filter(p => u[p.key]);
              return (
                <TableRow key={u.id} sx={{ opacity: u.is_active ? 1 : 0.5 }}>
                  <TableCell>
                    {u.full_name || '—'}
                    {isMe && <Chip label="Vous" size="small" sx={{ ml: 1, fontSize: 10 }} />}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{u.email}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {u.perm_users_manage ? (
                        <Chip label="Admin complet" size="small" color="primary" />
                      ) : activePerms.length === 0 ? (
                        <Chip label="Lecture seule" size="small" variant="outlined" color="default" />
                      ) : (
                        activePerms.map(p => (
                          <Chip key={p.key} label={p.label} size="small" variant="outlined" color="info" />
                        ))
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={u.is_active ? 'Actif' : 'Inactif'} size="small" color={u.is_active ? 'success' : 'error'} variant="outlined" />
                  </TableCell>
                  {isSuperAdmin && (
                    <TableCell align="right">
                      {!isMe && (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Tooltip title="Modifier les permissions">
                            <IconButton size="small" onClick={() => openPermDialog(u)}>
                              <EditIcon fontSize="small" color="primary" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={u.is_active ? 'Désactiver' : 'Activer'}>
                            <IconButton size="small" onClick={() => handleToggleActive(u)}>
                              {u.is_active ? <BlockIcon fontSize="small" color="warning" /> : <CheckCircleIcon fontSize="small" color="success" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Supprimer">
                            <IconButton size="small" onClick={() => setDeleteTarget(u)}>
                              <DeleteIcon fontSize="small" color="error" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* Invitation */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Inviter un collaborateur</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Email" type="email" fullWidth size="small" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} autoFocus />
          <TextField label="Nom complet (optionnel)" fullWidth size="small" value={inviteName} onChange={e => setInviteName(e.target.value)} />
          <Typography variant="caption" color="text.secondary">
            Un email avec un mot de passe temporaire sera envoyé. Vous pourrez configurer les permissions ensuite.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setInviteOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleInvite} disabled={!inviteEmail || inviting}>
            {inviting ? <CircularProgress size={20} color="inherit" /> : "Envoyer l'invitation"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permissions */}
      <Dialog open={!!permTarget} onClose={() => setPermTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Permissions — {permTarget?.full_name || permTarget?.email}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Activez les actions que cet utilisateur peut effectuer.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {/* Filet de séparation du thème (`divider`), comme les Divider et
                les lignes de tableau : un gris clair fixe ressortirait en barres
                lumineuses sur le fond sombre. */}
            {PERMS.map(p => (
              <Box key={p.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Box>
                  <Typography variant="body2" fontWeight="bold">{p.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{p.desc}</Typography>
                </Box>
                <Switch
                  checked={permTarget?.[p.key] ?? false}
                  onChange={() => handlePermToggle(p.key)}
                  color="primary"
                />
              </Box>
            ))}
          </Box>
          {permTarget?.perm_users_manage && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Cet utilisateur aura accès à toutes les fonctionnalités (admin complet).
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPermTarget(null)}>Annuler</Button>
          <Button variant="contained" onClick={handleSavePerms} disabled={permSaving}>
            {permSaving ? <CircularProgress size={20} color="inherit" /> : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation de suppression */}
      <Dialog open={!!deleteTarget} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Supprimer l'utilisateur</DialogTitle>
        <DialogContent>
          <Typography>Confirmer la suppression de <strong>{deleteTarget?.full_name || deleteTarget?.email}</strong> ?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Cette action est irréversible.</Typography>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteDialog}>Annuler</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={20} color="inherit" /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
